import './style.scss';

import {
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import cn from 'classnames';
import uniqBy from 'lodash/uniqBy';
import { useCallback, useEffect, useState } from 'react';
import { usePrevious } from 'react-use';
import { tailwindConfig } from 'tailwind-config';

import { SmartFloatingPortal as FloatingPortal } from '@/shared/ui/SmartFloatingPortal';

import { ApiPairs } from 'api';
import { IPairWallet } from 'api/apiPairs/models';
import { pairBots } from 'types/bots';
import { IDexPair } from 'types/pairs';
import { Spinner } from 'ui/Spinner/Spinner';
import { Switcher } from 'ui/Switcher/Switcher';

import { CogIcon } from 'assets/icons';

type BotSwitcher = {
  botId: number;
  active: boolean;
  name: string;
};

type PairTableConnectedBotsHeadProps = {
  isActive: boolean;
  selectedWallets: IPairWallet[];
  pair: IDexPair;
  onChangeSelectedWallets?: (newSelectedWallets: IPairWallet[]) => void;
};

export const PairTableConnectedBotsHead = ({
  isActive,
  selectedWallets,
  pair,
  onChangeSelectedWallets,
}: PairTableConnectedBotsHeadProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [botSwitchers, setBotSwitchers] = useState<BotSwitcher[]>([]);
  const prevSelectedWallets = usePrevious(selectedWallets);
  const [loadingBotId, setLoadingBotId] = useState<number | undefined>(undefined);

  const handleChangeOpened = useCallback(
    (opened: boolean) => {
      if (opened && !isActive) return;

      setIsOpen(opened);
    },
    [isActive],
  );

  const { x, y, refs, strategy, context } = useFloating({
    open: isOpen,
    onOpenChange: handleChangeOpened,
    middleware: [
      offset(10),
      flip({ fallbackAxisSideDirection: 'end', padding: 5 }),
      shift({ padding: 5 }),
    ],
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start',
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  useEffect(() => {
    const shouldUpdate =
      botSwitchers.length === 0 ||
      (!loadingBotId && JSON.stringify(selectedWallets) !== JSON.stringify(prevSelectedWallets));

    if (shouldUpdate) {
      setBotSwitchers(
        pairBots.map(bot => {
          const isActive =
            selectedWallets.length > 0 &&
            selectedWallets.every(wallet =>
              wallet.connected_bots?.some(connected => connected.id === bot.id),
            );

          return {
            botId: bot.id,
            active: isActive,
            name: bot.description,
          };
        }),
      );
    }

    // eslint-disable-next-line
  }, [selectedWallets]);

  const handleSwitchWallets = useCallback(
    async (botId: number, isConnected: boolean) => {
      if (!pair) return;
      if (loadingBotId) return;

      try {
        setBotSwitchers(prevBotSwitchers =>
          prevBotSwitchers.map(bot =>
            bot.botId === botId ? { ...bot, active: isConnected } : bot,
          ),
        );

        setLoadingBotId(botId);

        let extendedSelectedWallets = selectedWallets.map(el => ({
          ...el,
          connected_bots: el.connected_bots ? el.connected_bots.filter(el => el.id !== botId) : [],
        }));

        if (isConnected) {
          extendedSelectedWallets = extendedSelectedWallets.map(el => ({
            ...el,
            connected_bots: el.connected_bots.concat([
              pairBots.find(pairBot => pairBot.id === botId)!,
            ]),
          }));
        }

        if (onChangeSelectedWallets) {
          onChangeSelectedWallets(extendedSelectedWallets);
        }

        await ApiPairs.addWalletsToPair({
          pairId: pair.id,
          wallets: extendedSelectedWallets.map(el => ({
            id: el.id,
            bots_ids: uniqBy(
              el.connected_bots.map(el => el.id),
              el => el,
            ),
          })),
        });
      } catch (error) {
        console.log('error:', error);
      } finally {
        setLoadingBotId(undefined);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedWallets, botSwitchers, loadingBotId, onChangeSelectedWallets, pair],
  );

  return (
    <>
      <div
        className={cn('mm-table-th-connected-bots', { _active: isActive })}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <button>
          <CogIcon
            color={
              isActive ? tailwindConfig.theme.colors.primary : tailwindConfig.theme.colors['gray-2']
            }
          />
        </button>

        <span>Connected bots</span>
      </div>

      {isOpen && (
        <FloatingPortal>
          <div
            className="connected-bots-menu"
            ref={refs.setFloating}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
            }}
            {...getFloatingProps()}
          >
            <div className="connected-bots-menu__container">
              {botSwitchers.map(botSwitcher => (
                <div className="bot-switcher-item" key={botSwitcher.botId}>
                  {loadingBotId === botSwitcher.botId && (
                    <div className="bot-loading">
                      <Spinner size="mini" />
                    </div>
                  )}

                  {loadingBotId !== botSwitcher.botId && (
                    <Switcher
                      value={botSwitcher.active}
                      onChange={() => handleSwitchWallets(botSwitcher.botId, !botSwitcher.active)}
                    />
                  )}

                  <span className={cn({ _active: botSwitcher.active })}>{botSwitcher.name}</span>
                </div>
              ))}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};
